import { LIVE_EVENTS_CONFIG } from '@config/liveEvents';
import { useNow } from '@hooks/useNow';
import { cn } from '@lib/utils';
import type { ISODateTimeString } from '@models/api';
import { formatStartsIn } from '@utils/eventLiveness';

export interface LiveStateChipProps {
  /**
   * Which non-live state to show — live-now belongs to LiveBadge (L9).
   * `starting-soon` is the teal band badge for an upcoming event inside the
   * `startingSoonLeadMs` window; `soon` is the amber "Starts in Xh" countdown.
   */
  phase: 'soon' | 'ended' | 'starting-soon';
  /** Event start, required for the `soon` countdown copy. */
  startDate?: ISODateTimeString;
  /**
   * Parent-owned clock (from useEventPhase). When supplied, the countdown
   * derives from it and mounts no timer of its own — one clock per leaf.
   */
  now?: Date;
  /**
   * `on-image` floats in a cover photo's corner badge slot (adds elevation,
   * mirrors LiveBadge's variant); `inline` (default) sits in rows/meta.
   */
  variant?: 'inline' | 'on-image';
}

/**
 * Shared chip shape for the non-live state badges (soon countdown, grey ended,
 * teal "Starting soon"). One source so the whole chip family stays
 * byte-identical in shape. Module-local: every chip in the family renders from
 * this file, so the shape has no external consumer to export it to.
 */
const CHIP_BASE_CLASSES =
  'inline-flex w-fit shrink-0 items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium';

/**
 * The locked "Starting soon" copy — the single source for the teal band badge's
 * string. Owned here with the chip that renders it; re-exported from
 * `@components/live/EventPhaseBadge` so the Home rail's pill copy composes the
 * exact same word.
 */
export const STARTING_SOON_LABEL = 'Starting soon';

/**
 * Non-live state chip (spec §4): `soon` renders the dynamic locked copy from
 * `formatStartsIn` ("Starts in 2h" / "Starts in 25m") on an `mb-yellow-soft`
 * chip; `starting-soon` renders the teal `STARTING_SOON_LABEL` band badge
 * (Turquoise_700 on the chat-bubble tint) for the 6h before start; `ended`
 * renders grey "Ended". Never red — L9 reserves the red treatment for live-now.
 * The `on-image` variant is the corner badge on event-card covers (ruling
 * 2026-07-10); it carries no motion — the live-dot pulse and the space-slot
 * content-row rotation (W2, Dexter-amended §4.1) are the layer's only
 * persistent motions.
 */
export function LiveStateChip({ phase, startDate, now, variant = 'inline' }: LiveStateChipProps) {
  if (phase === 'ended') {
    return (
      <span
        className={cn(
          CHIP_BASE_CLASSES,
          'bg-gray-100 text-gray-500',
          variant === 'on-image' && 'shadow-soft',
        )}
      >
        Ended
      </span>
    );
  }
  if (phase === 'starting-soon') {
    return (
      <span
        className={cn(
          CHIP_BASE_CLASSES,
          'bg-mb-chat-bubble text-mb-turquoise-700',
          variant === 'on-image' && 'shadow-soft',
        )}
      >
        {STARTING_SOON_LABEL}
      </span>
    );
  }
  return <SoonCountdownChip startDate={startDate} now={now} variant={variant} />;
}

/**
 * Module-level leaf so `useNow` ticks only while a countdown is actually on
 * screen (spec §3 leaf-level ticking) — an `ended` chip never mounts a timer,
 * and neither does a countdown whose parent already ticks and passes `now`.
 */
function SoonCountdownChip({
  startDate,
  now,
  variant,
}: {
  startDate?: ISODateTimeString;
  now?: Date;
  variant: 'inline' | 'on-image';
}) {
  // Unconditional hook call (Rules of Hooks); `enabled` gates the timer.
  const internalNow = useNow(LIVE_EVENTS_CONFIG.nowTickMs, now === undefined);
  const effectiveNow = now ?? internalNow;
  if (!startDate) {
    return null; // callers gate on phase `soon`, which an event without dates can never reach
  }
  const text = formatStartsIn({ start_date: startDate }, effectiveNow);
  if (!text) {
    return null;
  }
  return (
    <span
      className={cn(
        CHIP_BASE_CLASSES,
        'bg-mb-yellow-soft text-gray-900 tabular-nums',
        variant === 'on-image' && 'shadow-soft',
      )}
    >
      {text}
    </span>
  );
}
